import type { ComponentProps } from "react";
import { useEffect } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { jt, t } from "ttag";

import { env } from "@/config/env";
import { billingQueriesStore } from "@/features/billing/api";
import {
  ReferralDashboardModal,
  UpdatePaymentMethodModal,
} from "@/features/billing/sections";
import { PaymentMethodTypeSelector } from "@/features/payments/components";
import { useKlarna } from "@/features/payments/hooks";
import { productsQueriesStore } from "@/features/products/api";
import { CompoundedMedicationDisclaimer } from "@/features/products/components/compounded-medications-disclaimer";
import {
  PaymentDisclaimer,
  PriceSelector,
  TotalSummary,
} from "@/features/products/components/shop-summary";
import { useShopContext } from "@/features/products/context";
import { useMarkShopSummaryVisited } from "@/features/products/hooks";
import { referralQueriesStore } from "@/features/referral/api";
import { CreditAmountSelector } from "@/features/referral/components";
import { LogoLoader } from "@/shared/components";
import { PAYMENT_METHOD_TYPE, PRICE_TYPE } from "@/shared/domains";
import type { PaymentMethod, Price } from "@/shared/domains";
import { useBoolean } from "@/shared/hooks";
import {
  Alert,
  Button,
  Card,
  CardIcon,
  DetailCard,
  GiftIcon,
  KlarnaIcon,
  MarkdownContent,
} from "@/shared/ui";
import { formatPrice, getBillingFrequencyVerbose } from "@/shared/utils";

const getPaymentMethodTypeOptions = (
  klarnaAvailable: boolean,
  defaultPaymentMethod?: PaymentMethod | null,
): ComponentProps<typeof PaymentMethodTypeSelector>["options"] => {
  const options = [];

  if (
    defaultPaymentMethod?.type === PAYMENT_METHOD_TYPE.CARD ||
    defaultPaymentMethod?.type === PAYMENT_METHOD_TYPE.APPLE_PAY
  ) {
    options.push({
      label: t`Card ending in ${defaultPaymentMethod.metadata.lastFourDigits}`,
      value: PAYMENT_METHOD_TYPE.CARD,
      icon: CardIcon,
    });
  }

  if (klarnaAvailable) {
    options.push({
      label: t`Klarna`,
      value: PAYMENT_METHOD_TYPE.KLARNA,
      icon: KlarnaIcon,
    });
  }

  return options;
};

const referralEnabled = env.VITE_REFERRAL_ENABLED;

export const ShopProductsSummary = () => {
  const {
    value: showUpdatePaymentMethodModal,
    setTrue: openUpdatePaymentMethodModal,
    setFalse: closeUpdatePaymentMethodModal,
  } = useBoolean(false);

  const {
    selectedProduct,
    selectedPrice,
    selectedCreditAmount,
    selectedPaymentMethodType,
    actions: {
      removeProduct,
      selectPrice,
      selectCreditAmount,
      selectPaymentMethodType,
    },
  } = useShopContext();

  const [
    { data: product, isLoading: isLoadingProduct, isSuccess: isSuccessProduct },
    {
      data: defaultPaymentMethod,
      isLoading: isLoadingDefaultPaymentMethod,
      isSuccess: isSuccessDefaultPaymentMethod,
    },
    { data: credit, isSuccess: isSuccessCredit },
  ] = useQueries({
    queries: [
      productsQueriesStore.products.shop._ctx.discounts(selectedProduct?.id),
      billingQueriesStore.payments._ctx.method,
      { ...referralQueriesStore.credit, enabled: referralEnabled },
    ],
  });

  useMarkShopSummaryVisited({ product });

  const {
    data: summary,
    isLoading: isLoadingSummary,
    isPlaceholderData: isPlaceholderSummary,
  } = useQuery({
    ...productsQueriesStore.products.shop._ctx.summary({
      priceId: selectedPrice?.id,
      creditAmount: selectedCreditAmount,
    }),
    placeholderData: (previousData) => previousData,
  });

  const isFetchingSummary = isLoadingSummary || isPlaceholderSummary;

  const {
    value: showReferralDashboardModal,
    setTrue: openReferralDashboardModal,
    setFalse: closeReferralDashboardModal,
  } = useBoolean(false);

  const { available: klarnaAvailable } = useKlarna({
    price: selectedPrice,
  });

  const paymentMethodTypeOptions = getPaymentMethodTypeOptions(
    klarnaAvailable && env.VITE_KLARNA_ENABLED,
    defaultPaymentMethod,
  );

  useEffect(() => {
    if (product?.prices.length === 1 && product?.prices[0]?.id) {
      handlePriceChange(product.prices[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const canApplyCreditAmount =
    isSuccessCredit && credit?.available > 0 && !credit.hasReachedMonthlyLimit;

  const hereButton = (
    <button
      type="button"
      key="here-button"
      className="font-semibold underline"
      onClick={openReferralDashboardModal}
    >
      {t`here`}
    </button>
  );

  const handlePriceChange = (id: Price["id"]) => {
    selectPaymentMethodType(undefined);

    if (!product) {
      return null;
    }

    const price = product.prices.find((price) => price.id === id);

    if (price) {
      selectPrice(price);
    }
  };

  return (
    <>
      <LogoLoader show={isLoadingProduct || isFetchingSummary} withOverlay />

      {isSuccessProduct && (
        <div className="flex w-full flex-col gap-6 self-center lg:max-w-xl">
          <Card.Root className="w-full border p-5">
            <Card.Content className="flex flex-col gap-4">
              <Card.Title weight="weak">{product.category.name}</Card.Title>

              <div className="flex flex-col gap-3">
                <DetailCard.Root
                  variant="borderLess"
                  className="flex-row gap-2.5 sm:gap-4"
                >
                  <DetailCard.Inset>
                    <DetailCard.Thumbnail
                      src={product.thumbnail ?? "#"}
                      className="max-w-20 rounded-lg sm:max-w-24 sm:rounded-none"
                    />
                  </DetailCard.Inset>

                  <div className="flex grow flex-col gap-2">
                    <div className="flex flex-col gap-1">
                      <DetailCard.Title className="line-clamp-1">
                        {product.name}
                      </DetailCard.Title>

                      <DetailCard.Description
                        className="line-clamp-2 text-xs sm:text-sm"
                        title={product.description}
                      >
                        <MarkdownContent>{product.description}</MarkdownContent>
                      </DetailCard.Description>
                    </div>

                    <Button
                      onClick={removeProduct}
                      variant="tertiary"
                      className="mt-auto self-start p-0 text-sm"
                    >
                      {t`Remove`}
                    </Button>
                  </div>

                  {product.prices.length === 1 && product.prices[0] && (
                    <div className="flex w-full max-w-fit flex-col gap-1 text-right">
                      <DetailCard.Title>
                        {formatPrice(product.prices[0].price)}
                      </DetailCard.Title>

                      {product.prices[0].type === PRICE_TYPE.RECURRING && (
                        <DetailCard.Description className="text-xs whitespace-nowrap sm:text-sm">
                          {t`Renews in`}
                          <br />
                          {getBillingFrequencyVerbose(
                            product.prices[0].durationInMonths,
                          )}
                        </DetailCard.Description>
                      )}
                    </div>
                  )}
                </DetailCard.Root>

                <div className="flex flex-col gap-9">
                  <PriceSelector
                    showDiscount
                    options={product.prices}
                    value={String(selectedPrice?.id)}
                    onValueChange={(value) => handlePriceChange(Number(value))}
                  />

                  {selectedPrice && (
                    <>
                      <DetailCard.Separator className="mx-0" />

                      <div className="space-y-4">
                        <PaymentMethodTypeSelector
                          options={paymentMethodTypeOptions}
                          loading={isLoadingDefaultPaymentMethod}
                          value={selectedPaymentMethodType ?? null}
                          onValueChange={(value) =>
                            selectPaymentMethodType(
                              value as PaymentMethod["type"],
                            )
                          }
                        />

                        {isSuccessDefaultPaymentMethod &&
                          !defaultPaymentMethod && (
                            <Button
                              variant="secondary"
                              size="lg"
                              className="w-full"
                              onClick={openUpdatePaymentMethodModal}
                            >
                              {t`Add card`}
                            </Button>
                          )}
                      </div>

                      {referralEnabled &&
                        (canApplyCreditAmount ? (
                          <CreditAmountSelector
                            value={selectedCreditAmount}
                            onChange={selectCreditAmount}
                            options={credit?.options}
                          />
                        ) : (
                          <Alert icon={GiftIcon}>
                            {jt`Refer friends to earn credit and unlock savings! Check it out ${hereButton}.`}
                          </Alert>
                        ))}
                    </>
                  )}
                </div>
              </div>
            </Card.Content>

            {summary && <TotalSummary summary={summary} />}
          </Card.Root>

          {selectedPrice && summary && selectedPaymentMethodType && (
            <>
              <PaymentDisclaimer price={selectedPrice} summary={summary} />
              <CompoundedMedicationDisclaimer />
            </>
          )}
        </div>
      )}

      {referralEnabled && (
        <ReferralDashboardModal
          show={showReferralDashboardModal}
          onClose={closeReferralDashboardModal}
        />
      )}

      <UpdatePaymentMethodModal
        show={showUpdatePaymentMethodModal}
        onClose={closeUpdatePaymentMethodModal}
      />
    </>
  );
};
